<template>
  <a-modal :open="visible" title="添加服务器" :footer="null" @cancel="onCancel">
    <a-form :model="form" @finish="onSubmit" layout="vertical">
      <a-form-item label="服务器名" name="name" :rules="[{ required: true, message: '请输入' }]">
        <a-input v-model:value="form.name" placeholder="如 nat-01" />
      </a-form-item>
      <a-form-item
        label="远程地址（IP / 域名）"
        name="ipPublic"
        extra="可填写公网 IP 或二级域名。复制节点配置时会实时解析域名并写入当前 IP。"
      >
        <a-input v-model:value="form.ipPublic" placeholder="如 1.2.3.4 或 hbt1.wch1.top" />
      </a-form-item>
      <a-form-item
        label="到期时间"
        name="expireAt"
        extra="留空 = 永不过期。过期后该服务器不能创建新节点；已有节点不受影响。"
      >
        <a-date-picker
          v-model:value="form.expireAt"
          show-time
          format="YYYY-MM-DD HH:mm:ss"
          placeholder="留空 = 永不过期"
          style="width:100%"
        />
      </a-form-item>
      <a-form-item
        label="总成本 (元)"
        name="totalCostYuan"
        extra="一次性 capex (服务器+配置)。留空 = 未设置，dashboard 会标记该服务器成本未知。"
      >
        <a-input-number
          v-model:value="form.totalCostYuan"
          :min="0"
          :precision="2"
          placeholder="可选,留空表示未设置"
          style="width:100%"
        />
      </a-form-item>
      <a-form-item
        label="下行带宽 (Mbps)"
        name="bandwidthDownMbps"
        extra="1 Mbps 约等于 128 KB/s；留空表示不判断跑满。"
      >
        <a-input-number
          v-model:value="form.bandwidthDownMbps"
          :min="0.01"
          :precision="2"
          placeholder="如 1"
          style="width:100%"
        />
      </a-form-item>
      <a-form-item
        label="服务器预置端口"
        extra="创建节点时优先按这里的顺序使用尚未占用的端口。可输入后回车，或使用逗号分隔。"
      >
        <a-select
          v-model:value="form.presetPorts"
          mode="tags"
          :token-separators="[',', '，', ' ']"
          placeholder="例如 10010, 10009, 44066"
        />
      </a-form-item>
      <a-button type="primary" html-type="submit" :loading="loading" block>生成安装命令</a-button>
    </a-form>
    <a-alert v-if="result" type="success" style="margin-top:16px" show-icon>
      <template #message>已创建 — 请在 NAT 上执行:</template>
      <template #description>
        <pre style="margin-top:8px;background:#f5f7fa;padding:8px;overflow:auto">{{ result.install_command }}</pre>
        <a-button size="small" @click="copyCmd">复制</a-button>
      </template>
    </a-alert>
  </a-modal>
</template>

<script setup lang="ts">
import { reactive, ref, watch } from 'vue';
import { message } from 'ant-design-vue';
import { createNode } from '../api/nodes';

const props = defineProps<{ visible: boolean }>();
const emit = defineEmits<{ (e: 'update:visible', v: boolean): void; (e: 'added'): void }>();
const form = reactive<{ name: string; ipPublic: string; expireAt: any; totalCostYuan: number | null; bandwidthDownMbps: number | null; presetPorts: string[] }>({
  name: '',
  ipPublic: '',
  expireAt: null,
  totalCostYuan: null,
  bandwidthDownMbps: null,
  presetPorts: [],
});
const result = ref<any>(null);
const loading = ref(false);

// Reset the form whenever the modal is reopened so a previous success
// state doesn't bleed into the next attempt, and so input fields clear.
watch(() => props.visible, (v) => {
  if (v) {
    form.name = '';
    form.ipPublic = '';
    form.expireAt = null;
    form.totalCostYuan = null;
    form.bandwidthDownMbps = null;
    form.presetPorts = [];
    result.value = null;
    loading.value = false;
  }
});

const onSubmit = async () => {
	const presetPorts = form.presetPorts
		.map((value) => String(value).trim())
		.filter(Boolean);
	if (presetPorts.some((value) => !/^\d+$/.test(value) || Number(value) < 1 || Number(value) > 65535)) {
		message.error('预置端口必须是 1-65535 的整数');
		return;
	}
  loading.value = true;
  try {
    // a-date-picker returns a Day.js instance; convert to ISO before
    // sending. Empty / null → never expires.
    const expireISO =
      form.expireAt && typeof form.expireAt.toDate === 'function'
        ? form.expireAt.toDate().toISOString()
        : '';
    // total_cost_cents is in cents (CNY 分); multiply the user's yuan
    // value by 100 and round to avoid float drift. Null = leave NULL.
    const totalCostCents =
      form.totalCostYuan == null ? null : Math.round(form.totalCostYuan * 100);
    const r = await createNode({
      name: form.name,
      ip_public: form.ipPublic,
      expire_at: expireISO,
      total_cost_cents: totalCostCents,
      bandwidth_down_mbps: form.bandwidthDownMbps == null ? null : form.bandwidthDownMbps,
		preset_ports: [...new Set(presetPorts.map(Number))],
    } as any);
    result.value = r.data;
    emit('added');
  } catch (e: any) {
    message.error(e.response?.data?.error?.message || '失败');
  } finally {
    loading.value = false;
  }
};
const onCancel = () => emit('update:visible', false);
const copyCmd = () => {
  navigator.clipboard.writeText(result.value.install_command);
  message.success('已复制');
};
</script>
